import { InfoOutlined } from '@mui/icons-material'
import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material'

import { FC } from 'react'

export interface ReadOnlyItemProps {
  /**
   * The class applied to the root element.
   */
  className?: string
  /**
   * The label of the item.
   */
  label: string
  /**
   * If `true`, the value can be shown in multiple lines.
   */
  multiline?: boolean
  /**
   * The value of the item.
   */
  value: string
  /**
   * The text to be displayed in the tooltip. If left empty or undefined, the info icon will not be displayed.
   */
  tooltip?: string

  sx?: SxProps<Theme>

  size?: 'medium' | 'small'

  link?: boolean
}

export const ReadOnlyItem: FC<ReadOnlyItemProps> = ({
  className,
  label,
  value,
  tooltip,
  multiline,
  sx,
  size,
  link,
}) => {
  return (
    <Box className={className} sx={sx}>
      <Box display="flex" alignItems="center">
        <Typography
          variant={size === 'small' ? 'caption' : 'body2'}
          color="textSecondary"
          sx={{
            fontSize: size === 'small' ? 10.5 : 11.5,
            fontWeight: 500,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            textDecoration: 'none',
            marginBottom: 0.6,
          }}
        >
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="right">
            <InfoOutlined color="primary" sx={{ marginLeft: 1, width: 13, height: 13, marginBottom: 0.6 }} />
          </Tooltip>
        )}
      </Box>
      <Typography
        component="p"
        variant={size === 'small' ? 'body2' : 'body1'}
        color="textPrimary"
        sx={{
          fontSize: size === 'small' ? 12.5 : 14,
          fontWeight: 400,
          lineHeight: 1.25,
          whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          textDecoration: link ? 'underline' : 'none',
        }}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  )
}
